/**
 * 5D Cost Timeliner — Playback controls for cost timeline + mini cost curve
 */
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight, DollarSign } from 'lucide-react';

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)} K`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export function FiveDTimeliner({ snapshots, currentIndex, onIndexChange, isPlaying, onTogglePlay, playSpeed, onSpeedChange }) {
  const total = snapshots?.length || 0;
  const current = snapshots?.[currentIndex];

  const costData = useMemo(() =>
    (snapshots || []).map(s => ({
      label: s.periodLabel,
      budget: s.metrics?.cumulativeBudget || s.metrics?.totalBudget || 0,
      actual: s.metrics?.cumulativeActual || s.metrics?.totalActual || 0,
    })),
  [snapshots]);

  if (!snapshots || total === 0) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500 text-sm" data-testid="cost-timeliner-empty">
        No cost timeliner data. Link CBS and generate snapshots.
      </div>
    );
  }

  const variance = (current?.metrics?.variance || 0);
  const cpi = current?.metrics?.cpi || 0;

  return (
    <div className="h-full flex flex-col px-3 py-2 gap-1" data-testid="cost-timeliner-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(0)} data-testid="cost-timeliner-start"><SkipBack className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(Math.max(0, currentIndex - 1))} data-testid="cost-timeliner-back"><ChevronLeft className="w-3.5 h-3.5" /></Button>
          <Button variant="default" size="sm" className="h-7 px-3 gap-1" onClick={onTogglePlay} data-testid="cost-timeliner-play">
            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(Math.min(total - 1, currentIndex + 1))} data-testid="cost-timeliner-forward"><ChevronRight className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(total - 1)} data-testid="cost-timeliner-end"><SkipForward className="w-3.5 h-3.5" /></Button>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">Speed:</span>
          <Select value={String(playSpeed)} onValueChange={v => onSpeedChange(Number(v))}>
            <SelectTrigger className="h-6 w-16 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0.25, 0.5, 1, 2, 4].map(s => <SelectItem key={s} value={String(s)}>{s}x</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Badge variant="outline" className="text-[10px] h-5">{current?.periodLabel || ''}</Badge>
        <div className="flex items-center gap-3 ml-auto text-[10px]">
          <span>Budget: <strong style={{ color: '#3B82F6' }}>{fmt(current?.metrics?.cumulativeBudget || current?.metrics?.totalBudget)}</strong></span>
          <span>Actual: <strong style={{ color: '#F97316' }}>{fmt(current?.metrics?.cumulativeActual || current?.metrics?.totalActual)}</strong></span>
          <span style={{ color: variance >= 0 ? '#22C55E' : '#EF4444' }}>Var: <strong>{fmt(variance)}</strong></span>
          <span>CPI: <strong style={{ color: cpi >= 1 ? '#22C55E' : cpi >= 0.9 ? '#F59E0B' : '#EF4444' }}>{cpi.toFixed(2)}</strong></span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-1">
        <span className="text-[10px] text-muted-foreground w-8 text-right font-mono">{currentIndex + 1}/{total}</span>
        <div className="flex-1 relative">
          <input
            type="range"
            min={0}
            max={total - 1}
            value={currentIndex}
            onChange={e => onIndexChange(Number(e.target.value))}
            className="w-full h-1.5 accent-cyan-500"
            data-testid="cost-timeliner-slider"
          />
        </div>
        <div className="w-56 h-12 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={costData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
              <Area type="monotone" dataKey="budget" stroke="#3B82F6" fill="#3B82F620" strokeWidth={1} dot={false} />
              <Area type="monotone" dataKey="actual" stroke="#F97316" fill="#F9731620" strokeWidth={1} dot={false} />
              {total > 1 && <ReferenceLine x={costData[currentIndex]?.label} stroke="#fff" strokeDasharray="2 2" strokeWidth={1} />}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
